import { v4 as uuidv4 } from "uuid";

import { isValidTimestamp } from "@/packages/shared/src/utils/date-utils";
import { enforceShape } from "@/src/lib/enforce-shape";
import { mergeNonNull } from "@/src/lib/merge-non-null";
import { withDurationMetric } from "@/src/lib/metrics";
import { customCamelCase, customSnakeCase } from "@/src/lib/string";
import { AddressRow } from "@/src/lib/types/tables.types";
import { validateState } from "@/src/lib/us-states";
import { Address, addressShape } from "@/src/models/address";
import { Group } from "@/src/models/group";
import { BaseRepository } from "@/src/repo/base-repository";

export const ADDRESS_TABLE_NAME = "address";
export const fullAddressSelect = `*`;

function normalizeState(state: string | null): string | null {
  if (!state) {
    return null;
  }

  return validateState(state);
}

export function mapAddressRowToAddress(addressRow: AddressRow): Address {
  const { ...rest } = addressRow;

  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(rest)) {
    mappedFields[customCamelCase(key)] = value;
  }

  return {
    ...mappedFields,
    placeId: addressRow.place_id ?? null,
  } as Address;
}

function mapAddressToAddressRow(address: Address): AddressRow {
  const { ...rest } = address;

  const cleanedAddress = enforceShape(rest, addressShape);
  const mappedFields: Record<string, any> = {};

  for (const [key, value] of Object.entries(cleanedAddress)) {
    mappedFields[customSnakeCase(key)] = value;

    if (key === "state") {
      mappedFields[customSnakeCase("state")] = normalizeState(value);
    }
  }

  return {
    ...mappedFields,
    place_id: address.placeId ?? null,
  } as AddressRow;
}

export class AddressRepository extends BaseRepository<Address, AddressRow> {
  constructor() {
    super({
      tableName: ADDRESS_TABLE_NAME,
      selectClause: fullAddressSelect,
      mapRowToModel: mapAddressRowToAddress,
      mapModelToRow: mapAddressToAddressRow,
    });
  }
}
export const addressRepository = new AddressRepository();
export const getAddress = addressRepository.get.bind(addressRepository);
export const getAllAddresses = addressRepository.getAll.bind(addressRepository);
export const insertAddress = addressRepository.insert.bind(addressRepository);
export const updateAddress = addressRepository.update.bind(addressRepository);
export const deleteAddress = addressRepository.delete.bind(addressRepository);

/**
 * Create or update an address
 * @param address - The address to create or update
 * @param group - The group to create or update the address in
 * @param overwrite - Whether to overwrite the existing address if it exists
 * @returns The created or updated address
 */
export const createOrUpdateAddress = withDurationMetric(async function createOrUpdateAddress(
  address: Address,
  group: Group,
  overwrite: boolean = true
) {
  if (!address.line1 && !address.city && !address.postalCode) {
    return null;
  }

  const hasExistingId = !!address.id;

  if (!hasExistingId) {
    address.id = uuidv4();
  }

  const existingAddress = hasExistingId ? await getAddress(address.id, group) : null;
  const now = new Date().toISOString();

  let addressToReturn: Address | null = null;

  if (existingAddress) {
    const mergedAddress = mergeNonNull(existingAddress, address, overwrite);

    mergedAddress.createdAt = existingAddress.createdAt;
    mergedAddress.updatedAt = now;

    addressToReturn = await updateAddress(mergedAddress.id, mergedAddress, group);
  } else {
    address.createdAt = isValidTimestamp(address.createdAt) ? address.createdAt : now;
    address.updatedAt = now;
    address.groupId = group.id;
    addressToReturn = await insertAddress(address, group);
  }

  return addressToReturn;
}, "createOrUpdateAddress");
